import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/** Formats a phone string into XXX.XXX.XXXX (dot-separated, matching the footer). Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
}

export function ApplyNowOptions() {
  const applyUrl = (usePropertyField('property_apply_now_url') as string) || '';
  const callTrackingPhone = (usePropertyField('property_call_tracking_phone') as string) || '';
  const defaultPhone = (usePropertyField('property_phone') as string) || '';
  const phone = callTrackingPhone || defaultPhone;

  const applyTitle = useContentToken('applynowoptions.apply_title');
  const applyCta = useContentToken('applynowoptions.apply_cta_label');
  const helpTitle = useContentToken('applynowoptions.help_title');
  const helpCtaLabel = useContentToken('applynowoptions.help_cta_label');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="ano-section">
      <div ref={sectionRef} className="reveal-fade-up ano-container">
        {/* Intro */}
        <div className="ano-intro">
          <RichTextContent
            path="applynowoptions.intro"
            label="Intro Text"
            defaultValue='<p>Ready to make <span data-type="field-token" data-field="property_name"></span> your new home? Apply now and take the first step toward enjoying spacious, modern apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>'
            as="div"
            className="ano-intro-text"
          />
        </div>

        <div className="ano-grid">
          {/* Apply Online Card */}
          <div className="ano-card">
            <EditableToken path="applynowoptions.apply_title" as="h3" className="ano-card-title">
              {applyTitle || 'Apply Online Now'}
            </EditableToken>
            {applyUrl ? (
              <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="ano-btn">
                <EditableToken path="applynowoptions.apply_cta_label" as="span">
                  {applyCta || 'Online Application'}
                </EditableToken>
              </a>
            ) : (
              <span className="ano-btn ano-btn--disabled">
                <EditableToken path="applynowoptions.apply_cta_label" as="span">
                  {applyCta || 'Application URL Not Available'}
                </EditableToken>
              </span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="ano-card">
            <EditableToken path="applynowoptions.help_title" as="h3" className="ano-card-title">
              {helpTitle || 'Need Help? Call Us'}
            </EditableToken>
            <a
              href={phone ? `tel:${phone}` : '#'}
              className="dniphone dniphonehref ano-phone"
            >
              <EditableToken path="applynowoptions.help_cta_label" as="span">
                {formatPhone(helpCtaLabel || phone)}
              </EditableToken>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .ano-section {
          padding: 2.5rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ano-section { padding: 3.5rem 0 4.5rem; }
        }
        /* Full-bleed row: the container spans the viewport with generous side
           gutters (matching the reference page) rather than a narrow centered
           column. */
        .ano-container {
          max-width: 100rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 640px) { .ano-container { padding: 0 2.5rem; } }
        @media (min-width: 1024px) { .ano-container { padding: 0 4rem; } }
        @media (min-width: 1280px) { .ano-container { padding: 0 6rem; } }
        /* Tablet: pull the row closer to the viewport edges so the two cards
           have more room. */
        @media (min-width: 768px) and (max-width: 1023px) { .ano-container { padding: 0 1.5rem; } }

        .ano-intro {
          text-align: center;
          max-width: 44rem;
          margin: 0 auto 2.5rem;
        }
        @media (min-width: 768px) { .ano-intro { margin-bottom: 3rem; } }
        .ano-intro-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
        }
        @media (min-width: 768px) { .ano-intro-text { font-size: 1.125rem; } }
        .ano-intro-text a { color: var(--vibe-color-primary); text-decoration: underline; }

        .ano-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          /* minmax(0, 1fr) lets tracks shrink below content min-width so the
             two cards can't force horizontal overflow at tablet widths. */
          .ano-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2.5rem; }
        }
        /* Tablet: tighten the gap between the two cards. */
        @media (min-width: 768px) and (max-width: 1023px) { .ano-grid { gap: 1.5rem; } }

        .ano-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 2rem;
          min-height: 18rem;
          padding: 3rem 2.5rem;
          background: #ffffff;
          border: 1px solid #ececef;
          border-radius: 0;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        /* Mobile: compact the card into a more landscape shape — drop the tall
           min-height, tighten the top/bottom padding, and pull the title and
           CTA closer together. */
        @media (max-width: 767px) {
          .ano-card {
            min-height: 0;
            gap: 1rem;
            padding: 1.75rem 2rem;
          }
        }
        /* Tablet keeps tighter horizontal padding so the CTA has room; the
           generous padding is reserved for desktop. The tall min-height is
           dropped and the gap tightened so the narrower tablet cards read as
           landscape (like desktop) rather than square. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .ano-card { min-height: 0; gap: 1.5rem; padding: 2.5rem 2rem; }
        }
        @media (min-width: 1024px) { .ano-card { padding: 4rem 5rem; } }
        .ano-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }

        .ano-card-title {
          font-family: var(--vibe-font-heading);
          /* Mobile: keep the title small enough to stay on a single line
             inside the card at narrow widths. */
          font-size: 1.375rem;
          font-weight: 500;
          line-height: 1.2;
          color: var(--vibe-color-text);
          margin: 0;
        }
        @media (min-width: 768px) { .ano-card-title { font-size: 2rem; } }
        /* Tablet: dial the title back so it stays on a single line. */
        @media (min-width: 768px) and (max-width: 1023px) { .ano-card-title { font-size: 1.5rem; } }

        /* Solid, filled pill CTA — matches the header "Contact Us" button
           (.nav-cta-btn) exactly: solid primary fill, on-primary label,
           text-base / font-normal / 0.15em uppercase, py-3.5 px-8, fully
           rounded, darkens + lifts on hover. */
        .ano-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          white-space: normal;
          text-align: center;
          max-width: 100%;
          padding: 0.875rem 2rem;
          border-radius: 9999px;
          text-decoration: none;
          cursor: pointer;
          background: var(--vibe-color-primary);
          border: 1.5px solid var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          box-shadow: none;
          transition: all 0.3s ease;
        }
        /* Below desktop, allow a long CTA label ("Online Application")
           to wrap instead of overflowing its card at tablet widths. */
        @media (max-width: 1023px) { .ano-btn { white-space: normal; } }
        /* Below desktop, dial the pill down a step so CTAs sit slightly smaller. */
        @media (max-width: 1023px) {
          .ano-btn { font-size: 0.875rem; padding: 0.75rem 1.5rem; }
        }
        .ano-btn:hover {
          background: color-mix(in srgb, var(--vibe-color-primary), black 12%);
          border-color: color-mix(in srgb, var(--vibe-color-primary), black 12%);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .ano-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        .ano-btn--disabled,
        .ano-btn--disabled:hover {
          background: var(--vibe-color-border, #e5e7eb);
          border-color: var(--vibe-color-border, #e5e7eb);
          color: var(--vibe-color-text-muted);
          cursor: default;
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }

        /* Phone rendered as bold accent text (not a button). */
        .ano-phone {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          color: var(--vibe-color-primary);
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        @media (min-width: 768px) { .ano-phone { font-size: 2rem; } }
        .ano-phone:hover { opacity: 0.8; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNowOptions',
  displayName: 'Apply Now Options',
  tokens: {
    'applynowoptions.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue:
        '<p>Ready to make <span data-type="field-token" data-field="property_name"></span> your new home? Apply now and take the first step toward enjoying spacious, modern apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>',
    },
    'applynowoptions.apply_title': {
      label: 'Apply Card Title',
      type: 'text',
      defaultValue: 'Apply Online Now',
    },
    'applynowoptions.apply_cta_label': {
      label: 'Apply CTA Label',
      type: 'text',
      defaultValue: 'Online Application',
    },
    'applynowoptions.help_title': {
      label: 'Help Card Title',
      type: 'text',
      defaultValue: 'Need Help? Call Us',
    },
    'applynowoptions.help_cta_label': {
      label: 'Phone CTA Label',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
  },
};

(ApplyNowOptions as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApplyNowOptions;
